// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Data-quality items + raw data preview panel:
// - elapsedDiffs: cached sorted positive elapsed-time deltas.
// - parserWarningDisplay / parserMetadataQualityItems: parser warning
//   rows and the parser metadata block used in the quality list.
// - engineLayoutOptionLabel / engineLayoutOptionConfig /
//   engineLayoutQualityItem: build the engine-layout disambiguation
//   item when a profile is needed to disambiguate probe counts.
// - dataQualityItems: full ordered list of {label, value} items for
//   the PDF report's data-quality section.
// - dataQualitySummary: cached short string ("3 gaps", "No large gaps").
// - renderRawDataPanel: the raw-data tab — summary cards, channel
//   inventory table, and the 1-minute preview table.

export function createDataQualityRenderer({
  escapeHtml,
  colors,
  seriesMeta,
  titleCase,
  quantile,
  extentFromValues,
  currentFlightDerivedCache,
  rawChannelKeys,
  rawChannelStats,
  createRawMetricDescriptor,
  currentEngineDescriptors,
  aircraftProfileForEntry,
  engineLayoutAmbiguityForData,
  formatElapsedTime,
  flightDurationSeconds,
  sampleRateSummary,
  formatPointTime,
  formatRawNumber,
  scoringRole,
  getFlightData,
  getCurrentParserMetadata,
  getSelectedFlight,
  getSourceMonitor,
  getSourceRows,
  getSourceRawRows,
  getEndpointTrimInfo,
  appReadOnlyMode = () => false,
  readOnlyActionMessage = () => "This action is disabled in demo mode.",
}) {
  function elapsedDiffs() {
    const cache = currentFlightDerivedCache();
    if (cache.elapsedDiffs) return cache.elapsedDiffs;
    const values = getFlightData()
      .map((point) => point.elapsedSeconds)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    const diffs = [];
    for (let index = 1; index < values.length; index += 1) {
      const diff = values[index] - values[index - 1];
      if (diff > 0) diffs.push(diff);
    }
    cache.elapsedDiffs = diffs;
    return diffs;
  }

  function parserWarningDisplay(warning) {
    const severity = warning?.severity ? titleCase(String(warning.severity)) : "Warning";
    const code = warning?.code ? `${warning.code}: ` : "";
    const field = warning?.field ? `, ${warning.field}` : "";
    const row = Number.isInteger(warning?.rowIndex) ? ` (row ${warning.rowIndex + 1}${field})` : "";
    return `${severity} - ${code}${warning?.message ?? "Parser warning"}${row}`;
  }

  function parserMetadataQualityItems(metadata = getCurrentParserMetadata()) {
    if (!metadata) {
      return [{
        label: "Parser metadata",
        value: "Not available for this source",
      }];
    }
    const warnings = Array.isArray(metadata.parserWarnings) ? metadata.parserWarnings : [];
    const warningCount = warnings.length;
    const items = [
      {
        label: "Parser",
        value: metadata.parserId ?? "Unknown",
      },
      {
        label: "Parser version",
        value: metadata.parserVersion ?? "n/a",
      },
      {
        label: "Schema version",
        value: metadata.schemaVersion ?? "n/a",
      },
      {
        label: "Parser confidence",
        value: metadata.parserConfidence ?? "n/a",
      },
      {
        label: "Parser warnings",
        value: warningCount
          ? `${warningCount.toLocaleString()} parser warning${warningCount === 1 ? "" : "s"}`
          : "No parser warnings",
      },
    ];
    warnings.slice(0, 3).forEach((warning, index) => {
      items.push({
        label: `Parser warning ${index + 1}`,
        value: parserWarningDisplay(warning),
      });
    });
    if (warningCount > 3) {
      const hiddenCount = warningCount - 3;
      items.push({
        label: "Parser warnings hidden",
        value: `${hiddenCount.toLocaleString()} more parser warning${hiddenCount === 1 ? "" : "s"}`,
      });
    }
    return items;
  }

  function engineLayoutOptionLabel(option) {
    return {
      "single-8": "Single 8-cylinder engine",
      "twin-4": "Twin 4-cylinder engines",
      "single-12": "Single 12-cylinder engine",
      "twin-6": "Twin 6-cylinder engines",
      "single-16": "Single 16-cylinder engine",
      "twin-8": "Twin 8-cylinder engines",
    }[option] ?? titleCase(String(option ?? "").replace(/-/g, " "));
  }

  function engineLayoutOptionConfig(option) {
    const match = String(option ?? "").match(/^(single|twin)-(\d+)$/);
    if (!match) return null;
    const mode = match[1];
    const cylinders = Number(match[2]);
    if (!Number.isInteger(cylinders) || cylinders <= 0) return null;
    if (mode === "single") {
      const group = Array.from({ length: cylinders }, (_, index) => index + 1);
      return {
        count: 1,
        engineIds: ["engine1"],
        cylindersPerEngine: [cylinders],
        egtGroups: [group],
        chtGroups: [group],
      };
    }
    const total = cylinders * 2;
    const left = Array.from({ length: cylinders }, (_, index) => index + 1);
    const right = Array.from({ length: cylinders }, (_, index) => cylinders + index + 1).filter((number) => number <= total);
    return {
      count: 2,
      engineIds: ["left", "right"],
      cylindersPerEngine: [cylinders, cylinders],
      egtGroups: [left, right],
      chtGroups: [left, right],
    };
  }

  function engineLayoutQualityItem() {
    const selectedFlight = getSelectedFlight();
    const currentParserMetadata = getCurrentParserMetadata();
    const ambiguity = engineLayoutAmbiguityForData(getFlightData(), aircraftProfileForEntry(selectedFlight), {
      parserId: currentParserMetadata?.parserId ?? selectedFlight?.parser,
      parserMetadata: currentParserMetadata,
    });
    if (!ambiguity?.ambiguous) return null;
    const options = (ambiguity.candidates ?? []).map((option) => ({
      id: option,
      label: engineLayoutOptionLabel(option),
      config: engineLayoutOptionConfig(option),
    })).filter((option) => option.config);
    return {
      label: "Engine layout",
      value: `${ambiguity.probeCount} thermal probes need aircraft profile mapping`,
      className: "is-warning",
      detail: "Probe count alone cannot distinguish these layouts. Add one engineLayout block to the aircraft profile so future imports use the correct engine/cylinder grouping.",
      options,
    };
  }

  function dataQualityItems() {
    const flightData = getFlightData();
    const endpointTrimInfo = getEndpointTrimInfo();
    const selectedFlight = getSelectedFlight();
    const diffs = elapsedDiffs();
    const medianInterval = quantile(diffs, 0.5) ?? 1;
    const gapThreshold = Math.max(5, medianInterval * 3);
    const gaps = diffs.filter((diff) => diff > gapThreshold);
    const maxGap = extentFromValues(gaps)?.max ?? 0;
    const channelKeys = rawChannelKeys();
    const describeRaw = createRawMetricDescriptor(currentEngineDescriptors());
    const channelsWithMissing = channelKeys
      .map((key) => {
        const [label] = describeRaw(key);
        return { label, missing: rawChannelStats(key).missing };
      })
      .filter((item) => item.missing > 0)
      .sort((a, b) => b.missing - a.missing)
      .slice(0, 4);
    const zeroLikeChannels = channelKeys
      .filter((key) => /^(egt|cht)\d?$/.test(key))
      .map((key) => {
        const [label] = describeRaw(key);
        const count = flightData.filter((point) => Number.isFinite(point[key]) && point[key] <= 0).length;
        return { label, count };
      })
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
    const displayTimes = new Map();
    flightData.forEach((point) => {
      if (!point.displayTime) return;
      displayTimes.set(point.displayTime, (displayTimes.get(point.displayTime) ?? 0) + 1);
    });
    const repeatedClockRows = [...displayTimes.values()].reduce((total, count) => total + Math.max(0, count - 1), 0);
    const items = [
      {
        label: "Rows parsed",
        value: `${flightData.length.toLocaleString()} normalized samples${endpointTrimInfo.total ? ` after endpoint trim` : ""}`,
      },
      {
        label: "Elapsed span",
        value: `${formatElapsedTime(flightDurationSeconds())} at ${sampleRateSummary()}`,
      },
      {
        label: "Timeline gaps",
        value: gaps.length
          ? `${gaps.length.toLocaleString()} gap${gaps.length === 1 ? "" : "s"}, largest ${formatElapsedTime(maxGap)}`
          : "No large elapsed-time gaps detected",
      },
      {
        label: "Repeated clock times",
        value: repeatedClockRows
          ? `${repeatedClockRows.toLocaleString()} repeated display-time rows, expected on higher-rate logs`
          : "No repeated display-time rows",
      },
    ];
    items.push(...parserMetadataQualityItems());
    const layoutItem = engineLayoutQualityItem();
    if (layoutItem) items.push(layoutItem);
    const sourceRawRows = getSourceRawRows();
    const sourceRows = getSourceRows();
    if (sourceRawRows > sourceRows) {
      items.push({
        label: "1 Hz resampling",
        value: `${sourceRawRows.toLocaleString()} raw rows reduced to ${sourceRows.toLocaleString()} 1 Hz samples`,
      });
    }
    if (channelsWithMissing.length) {
      items.push({
        label: "Most missing channels",
        value: channelsWithMissing.map((item) => `${item.label}: ${item.missing.toLocaleString()}`).join(", "),
      });
    }
    if (zeroLikeChannels.length) {
      items.push({
        label: "Zero-value temperature samples",
        value: zeroLikeChannels.map((item) => `${item.label}: ${item.count.toLocaleString()}`).join(", "),
      });
    }
    if (endpointTrimInfo.total) {
      items.push({
        label: "Endpoint trim",
        value: `Removed first/last ${endpointTrimInfo.seconds} second of samples (${endpointTrimInfo.leading.toLocaleString()} leading, ${endpointTrimInfo.trailing.toLocaleString()} trailing)`,
      });
    }
    if (selectedFlight?.segment) {
      items.push({
        label: "Segment source",
        value: "This entry is a detected engine-run segment from a larger day log",
      });
    }
    return items;
  }

  function dataQualitySummary() {
    const cache = currentFlightDerivedCache();
    if (cache.dataQualitySummary) return cache.dataQualitySummary;
    const diffs = elapsedDiffs();
    const medianInterval = quantile(diffs, 0.5) ?? 1;
    const gapThreshold = Math.max(5, medianInterval * 3);
    const gapCount = diffs.filter((diff) => diff > gapThreshold).length;
    const flightData = getFlightData();
    const zeroTempCount = rawChannelKeys()
      .filter((key) => /^(egt|cht)\d?$/.test(key))
      .reduce((total, key) => {
        return total + flightData.filter((point) => Number.isFinite(point[key]) && point[key] <= 0).length;
      }, 0);
    cache.dataQualitySummary = gapCount
      ? `${gapCount} gap${gapCount === 1 ? "" : "s"}`
      : zeroTempCount
        ? `${zeroTempCount.toLocaleString()} zero temp samples`
        : "No large gaps";
    return cache.dataQualitySummary;
  }

  function renderRawDataPanel() {
    const summary = document.querySelector("#rawSummaryGrid");
    const inventory = document.querySelector("#channelInventoryBody");
    const previewHead = document.querySelector("#parsedPreviewHead");
    const previewBody = document.querySelector("#parsedPreviewBody");
    const previewNote = document.querySelector("#rawPreviewNote");
    const downloadButton = document.querySelector("#action-download-normalized-csv");
    if (!summary || !inventory || !previewHead || !previewBody) return;
    const flightData = getFlightData();
    if (downloadButton) {
      const readOnly = appReadOnlyMode();
      downloadButton.disabled = readOnly || !flightData?.length;
      downloadButton.toggleAttribute("aria-disabled", downloadButton.disabled);
      downloadButton.toggleAttribute("data-demo-locked", readOnly);
      downloadButton.title = readOnly
        ? readOnlyActionMessage("Downloading normalized CSV files")
        : flightData?.length
          ? "Download normalized CSV"
          : "Load a flight log before downloading the normalized CSV.";
    }

    const channelKeys = rawChannelKeys();
    const describeRaw = createRawMetricDescriptor(currentEngineDescriptors());
    const first = flightData[0];
    const last = flightData.at(-1);
    const selectedFlight = getSelectedFlight();
    const summaryItems = [
      ["Aircraft", selectedFlight?.aircraft ?? "Sample"],
      ["Monitor", selectedFlight?.monitor ?? getSourceMonitor()],
      ["Time range", first && last ? `${formatPointTime(first)} - ${formatPointTime(last)}` : "n/a"],
      ["Duration", formatElapsedTime(flightDurationSeconds())],
      ["Samples", flightData.length.toLocaleString()],
      ["Channels", channelKeys.length.toLocaleString()],
    ];
    summary.innerHTML = summaryItems.map(([label, value]) => `
      <article class="raw-summary-card">
        <span>${escapeHtml(label)}</span>
        <strong>${escapeHtml(value)}</strong>
      </article>
    `).join("");

    inventory.innerHTML = channelKeys.map((key) => {
      const [label, unit, digits] = describeRaw(key);
      const stats = rawChannelStats(key);
      return `
        <tr>
          <td style="color:${seriesMeta[key]?.color ?? colors.text}">${escapeHtml(label)}</td>
          <td>${escapeHtml(unit || "-")}</td>
          <td>${formatRawNumber(stats.min, digits)}</td>
          <td>${formatRawNumber(stats.avg, digits)}</td>
          <td>${formatRawNumber(stats.max, digits)}</td>
          <td>${stats.missing.toLocaleString()}</td>
          <td>${scoringRole(key)}</td>
        </tr>
      `;
    }).join("");

    const previewKeys = channelKeys
      .filter((key) => key !== "egt" && key !== "cht")
      .slice(0, 18);
    const previewIntervalSeconds = 60;
    const previewRows = [];
    let nextPreviewElapsed = null;
    flightData.forEach((point) => {
      const elapsed = Number.isFinite(point?.elapsedSeconds) ? point.elapsedSeconds : null;
      if (!previewRows.length) {
        previewRows.push(point);
        nextPreviewElapsed = elapsed === null ? previewIntervalSeconds : elapsed + previewIntervalSeconds;
        return;
      }
      if (elapsed === null || nextPreviewElapsed === null || elapsed + 1e-6 < nextPreviewElapsed) return;
      previewRows.push(point);
      nextPreviewElapsed += previewIntervalSeconds;
    });
    const lastPreviewPoint = previewRows.at(-1);
    const lastFlightPoint = flightData.at(-1);
    if (lastFlightPoint && lastPreviewPoint !== lastFlightPoint) previewRows.push(lastFlightPoint);
    previewHead.innerHTML = `
      <tr>
        <th>Time</th>
        <th>Elapsed</th>
        ${previewKeys.map((key) => `<th>${escapeHtml(describeRaw(key)[0])}</th>`).join("")}
      </tr>
    `;
    previewBody.innerHTML = previewRows.map((point) => `
      <tr>
        <td>${escapeHtml(formatPointTime(point))}</td>
        <td>${escapeHtml(formatElapsedTime(point.elapsedSeconds))}</td>
        ${previewKeys.map((key) => `<td>${escapeHtml(formatRawNumber(point[key], describeRaw(key)[2]))}</td>`).join("")}
      </tr>
    `).join("");
    if (previewNote) {
      previewNote.textContent = `Showing ${previewRows.length.toLocaleString()} rows at 1-minute intervals, not the full 1 Hz normalized log`;
    }
  }

  return {
    elapsedDiffs,
    parserWarningDisplay,
    parserMetadataQualityItems,
    engineLayoutOptionLabel,
    engineLayoutOptionConfig,
    engineLayoutQualityItem,
    dataQualityItems,
    dataQualitySummary,
    renderRawDataPanel,
  };
}
